import { useCallback, useState } from 'react';

import {
  type AccountLimitsVersion,
  AccountLimitsVersionSchema,
  type SaveSettingsRequest,
  type SettingsVersion,
  SettingsVersionSchema,
  type SubscriptionId,
  type UpdateAccountLimitsRequest,
} from '../../api';
import { type ApiError, toApiError } from '../../lib/api/api-client';
import { useApiClient } from '../../lib/api/api-context';
import { useKeyPerBody } from '../../lib/api/idempotency-key';

import { settingsPath } from './settings-api';

/** What the Save button has done. */
export type SaveState =
  | { readonly status: 'idle' }
  | { readonly status: 'saving' }
  | {
      readonly status: 'saved';
      /** `null` when the channel settings were unchanged, so only the limit was sent. */
      readonly version: SettingsVersion | null;
      /** `null` when the account limit was not touched. */
      readonly limits: AccountLimitsVersion | null;
    }
  | {
      readonly status: 'failed';
      readonly error: ApiError;
      /** Set when the channel settings were saved and only the account limit failed. */
      readonly settingsSaved: SettingsVersion | null;
    };

/** What one Save did: each part that was saved, and the failure that stopped it, if any. */
export interface SaveResult {
  readonly version: SettingsVersion | null;
  readonly limits: AccountLimitsVersion | null;
  readonly error: ApiError | null;
}

/**
 * Saves E7: `PUT …/settings` (a new version, M2 R16) when the channel settings changed, then
 * `PUT /account/limits` when the trader changed the account-wide daily limit. The limit is never
 * part of a channel version (M2 R12). When the settings were saved and the limit failed, the
 * outcome carries both, so a retry sends only the limit.
 */
export function useSaveSettings(subscriptionId: SubscriptionId): {
  readonly state: SaveState;
  readonly save: (
    settings: SaveSettingsRequest | null,
    limits: UpdateAccountLimitsRequest | null,
  ) => Promise<SaveResult>;
  readonly reset: () => void;
} {
  const api = useApiClient();
  const [state, setState] = useState<SaveState>({ status: 'idle' });
  const settingsKey = useKeyPerBody();
  const limitsKey = useKeyPerBody();

  const save = useCallback(
    async (
      settings: SaveSettingsRequest | null,
      limits: UpdateAccountLimitsRequest | null,
    ): Promise<SaveResult> => {
      setState({ status: 'saving' });
      let version: SettingsVersion | null = null;
      if (settings !== null) {
        try {
          version = await api.put(
            settingsPath(subscriptionId),
            {
              guard: { kind: 'authenticated', idempotencyKey: settingsKey.keyFor(settings) },
              body: settings,
            },
            SettingsVersionSchema,
          );
          settingsKey.settle(null);
        } catch (caught) {
          const error = toApiError(caught);
          settingsKey.settle(error);
          setState({ status: 'failed', error, settingsSaved: null });
          return { version: null, limits: null, error };
        }
      }
      let limitsVersion: AccountLimitsVersion | null = null;
      if (limits !== null) {
        try {
          limitsVersion = await api.put(
            '/account/limits',
            {
              guard: { kind: 'authenticated', idempotencyKey: limitsKey.keyFor(limits) },
              body: limits,
            },
            AccountLimitsVersionSchema,
          );
          limitsKey.settle(null);
        } catch (caught) {
          const error = toApiError(caught);
          limitsKey.settle(error);
          setState({ status: 'failed', error, settingsSaved: version });
          return { version, limits: null, error };
        }
      }
      setState({ status: 'saved', version, limits: limitsVersion });
      return { version, limits: limitsVersion, error: null };
    },
    [api, subscriptionId, settingsKey, limitsKey],
  );

  const reset = useCallback(() => {
    setState((current) =>
      current.status === 'saved' || current.status === 'failed' ? { status: 'idle' } : current,
    );
  }, []);

  return { state, save, reset };
}
